import React, { Fragment, useContext, useEffect, useState } from "react";

import { Preview } from "@creatomate/preview";
import { ElementSource } from "chq";
import { useProfile } from "app/context";
import { isMicrosoft, isSantander, Trans } from "app/utils";
import { VIDEO_TYPE } from "components/stories/video-studio/utils/constants";

import createElement from "components/stories/video-studio/utils/creatomate/createElement";
import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import { usePreviewTime } from "components/stories/video-studio/contexts/TimeContext";

import CarouselLibrary from "./CarouselLibrary";
import { defaultEmojiData, defaultFrameData, defaultIntroVideoData, defaultLogoData, defaultOutroVideoData, microsoftIntroVideoData, microsoftLogoData, microsoftOutroVideoData, santanderIntroVideoData, santanderLogoData, santanderOutroVideoData } from "./assets";
import { isSubtitleText } from "../subtitle/utils";

interface LibraryProps {
  preview: Preview;
}

const Library: React.FC<LibraryProps> = ({ preview }) => {
  const { time } = usePreviewTime();
  const { data: { mediaType }, handleUpdateData } = useContext(CreatomateContext);
  const { profile: { organization } } = useProfile();

  const [logoData, setLogoData] = useState(defaultLogoData);
  const [introData, setIntroData] = useState(defaultIntroVideoData);
  const [outroData, setOutroData] = useState(defaultOutroVideoData);

  useEffect(() => {
    if (isMicrosoft(organization.name)) {
      setLogoData([...microsoftLogoData, ...defaultLogoData]);
      setIntroData([...microsoftIntroVideoData, ...defaultIntroVideoData]);
      setOutroData([...microsoftOutroVideoData, ...defaultOutroVideoData]);
    } else if (isSantander(organization.name)) {
      setLogoData([...santanderLogoData, ...defaultLogoData]);
      setIntroData([...santanderIntroVideoData, ...defaultIntroVideoData]);
      setOutroData([...santanderOutroVideoData, ...defaultOutroVideoData]);
    } else {
      setLogoData(defaultLogoData);
      setIntroData(defaultIntroVideoData);
      setOutroData(defaultOutroVideoData);
    }
  }, [organization]);

  const handleAssetSelected = async (asset: Record<string, any>) => {
    const source = preview.getSource();
    const hasSubtitles = source?.elements?.some((element: ElementSource) => (
      isSubtitleText(element.name)
    ));

    let duration;
    if (time + 5 <= (preview.state?.duration || 0)) duration = 5;
    if (asset.type === "video") handleUpdateData({ blockSubtitles: !hasSubtitles });
    await createElement({ duration, time, source: asset.url, ...asset }, preview);
  };

  return (
    <Fragment>
      <div className="Captions-Header__Title">
        <div className="Captions-Header__Actions">
          <h2><Trans>Library</Trans></h2>
        </div>
      </div>
      <div>
        <div>
          <CarouselLibrary
            assets={defaultEmojiData}
            className="library-emj"
            onAssetClick={handleAssetSelected}
            title="Emojis"
          />
        </div>
        <div>
          <CarouselLibrary
            assets={defaultFrameData}
            className="library-frm"
            onAssetClick={handleAssetSelected}
            title="Frames"
          />
        </div>
        <div>
          <CarouselLibrary
            assets={logoData}
            className="library-logo"
            onAssetClick={handleAssetSelected}
            title="Logo"
          />
        </div>
        {mediaType === VIDEO_TYPE && (
          <>
            <div>
              <CarouselLibrary
                assets={introData}
                className="library-intro"
                onAssetClick={handleAssetSelected}
                title="Intro"
                videoAssets
              />
            </div>
            <div>
              <CarouselLibrary
                assets={outroData}
                className="library-outro"
                onAssetClick={handleAssetSelected}
                title="Outro"
                videoAssets
              />
            </div>
          </>
        )}
      </div>
    </Fragment>
  );
};

export default Library;
